(() => {
  const storageKey = 'sk.brand-saves.v1';
  const safeUrl = value => {
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin && /^\/(?:fi\/|sv\/|da\/|no\/)?(?:products|collections)\//.test(url.pathname) ? url.pathname : null;
    } catch { return null; }
  };
  const safeImage = value => {
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch { return null; }
  };
  class BrandSaves extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.root = this.closest('brand-collection');
      this.dialog = this.querySelector('dialog');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.state = { brands: [], products: [] };
      this.read();
      this.root.addEventListener('click', event => this.onClick(event), options);
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('brand-saved-open'), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.root.querySelectorAll('.brand-price[open], .brand-category-menu[open]').forEach(details => { details.open = false; details.querySelector('summary').focus(); });
      }, options);
      document.addEventListener('click', event => {
        this.root.querySelectorAll('.brand-price[open], .brand-category-menu[open]').forEach(details => { if (!details.contains(event.target) || event.target.closest('a')) details.open = false; });
      }, options);
      window.addEventListener('storage', event => {
        if (event.key !== storageKey && event.key !== null) return;
        this.read();
        this.refresh();
        if (this.dialog.open) this.render();
      }, options);
      this.querySelectorAll('[data-brand-save], [data-brand-saved-open]').forEach(button => { button.hidden = false; });
      this.refresh();
    }

    disconnectedCallback() {
      this.dialog?.close();
      this.events?.abort();
      this.events = null;
    }

    read() {
      try {
        const value = JSON.parse(localStorage.getItem(storageKey) || '{}');
        for (const kind of ['brands', 'products']) {
          this.state[kind] = Array.isArray(value[kind]) ? value[kind].filter(item => item && /^\d+$/.test(item.id) && typeof item.title === 'string' && safeUrl(item.url)).slice(0, 100).map(item => ({ ...item, id: String(item.id), title: item.title.slice(0, 300) })) : [];
        }
      } catch { this.state = { brands: [], products: [] }; }
    }

    update(kind, item) {
      const items = this.state[kind];
      const exists = items.some(value => value.id === item.id);
      const next = { ...this.state, [kind]: exists ? items.filter(value => value.id !== item.id) : [...items.slice(-99), item] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); }
      catch { this.querySelector('[data-brand-save-status]').textContent = this.dataset.error; return; }
      this.state = next;
      this.refresh();
      if (this.dialog.open) this.render();
    }

    refresh() {
      const saved = this.state?.brands.some(item => item.id === this.dataset.brandId);
      this.querySelector('[data-brand-save]').setAttribute('aria-pressed', String(Boolean(saved)));
      this.querySelector('[data-brand-save-label]').textContent = saved ? this.dataset.saved : this.dataset.save;
      this.querySelector('[data-brand-save]').setAttribute('aria-label', saved ? this.dataset.saved : this.dataset.save);
      this.querySelector('[data-brand-save]').title = saved ? this.dataset.saved : this.dataset.save;
      this.querySelector('[data-brand-saved-count]').textContent = (this.state?.products.length || 0) + (this.state?.brands.length || 0);
      this.root.querySelectorAll('[data-brand-product-save]').forEach(button => {
        button.hidden = false;
        button.setAttribute('aria-pressed', String(Boolean(this.state?.products.some(item => item.id === button.dataset.id))));
      });
    }

    render() {
      for (const kind of ['brands', 'products']) {
        const list = this.querySelector('[data-saved-' + kind + ']');
        list.replaceChildren();
        if (!this.state[kind].length) {
          const empty = document.createElement('p');
          empty.textContent = this.dataset.empty;
          list.append(empty);
        }
        for (const item of this.state[kind]) {
          const row = this.querySelector('[data-saved-' + (kind === 'brands' ? 'brand' : 'product') + '-template]').content.cloneNode(true);
          row.querySelector('[data-saved-link]').href = safeUrl(item.url);
          row.querySelector('[data-saved-title]').textContent = item.title;
          const remove = row.querySelector('[data-saved-remove]');
          remove.dataset.kind = kind;
          remove.dataset.id = item.id;
          remove.title = this.dataset.remove;
          remove.setAttribute('aria-label', this.dataset.remove + ': ' + item.title);
          const image = row.querySelector('img');
          if (image) {
            if (safeImage(item.image)) image.src = safeImage(item.image);
            else image.remove();
            const choose = row.querySelector('[data-quick-shop-open]');
            if (item.available) {
              choose.dataset.productUrl = safeUrl(item.url);
              choose.setAttribute('aria-label', this.dataset.choose + ': ' + item.title);
              choose.title = this.dataset.choose;
            } else choose.remove();
          }
          list.append(row);
        }
      }
      document.querySelector('quick-shop')?.enableButtons?.(this.dialog);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.matches('[data-brand-product-save]')) {
        this.update('products', { id: button.dataset.id, url: button.dataset.url, title: button.dataset.title, image: button.dataset.image, available: button.dataset.available === 'true' });
      } else if (button.matches('[data-brand-save]')) {
        this.update('brands', { id: this.dataset.brandId, title: this.dataset.brandName, url: this.dataset.brandUrl });
      } else if (button.matches('[data-brand-saved-open]')) {
        this.render();
        this.dialog.showModal();
        document.documentElement.classList.add('brand-saved-open');
      } else if (button.matches('[data-brand-saved-close]')) {
        this.dialog.close();
      } else if (button.matches('[data-saved-remove]')) {
        const kind = button.dataset.kind;
        const item = this.state[kind]?.find(value => value.id === button.dataset.id);
        if (item) this.update(kind, item);
        this.querySelector('[data-brand-saved-close]').focus();
      } else if (button.matches('[data-quick-shop-open]') && this.dialog.contains(button)) {
        event.stopPropagation();
        this.dialog.close();
        const quickShop = document.querySelector('quick-shop');
        quickShop?.open?.(button);
        // The saved row is now in a closed dialog; return focus to its visible entry point.
        if (quickShop) quickShop.trigger = this.querySelector('[data-brand-saved-open]');
      }
    }
  }
  if (!customElements.get('brand-saves')) customElements.define('brand-saves', BrandSaves);
})();
